import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ChevronDown, LayoutGrid, List, Search, SearchX, Smartphone } from "lucide-react";
import { useMemo, useState } from "react";
import { DeviceCard, DeviceRow } from "@/components/device-card";
import { PageHeader, PageToolbar } from "@/components/page-header";
import { DEVICE_STATUS, StatusDot } from "@/components/status-dot";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { SegmentedControl, SegmentedControlItem } from "@/components/ui/segmented-control";
import { Skeleton } from "@/components/ui/skeleton";
import { useDeviceStream } from "@/hooks/use-device-stream";
import { trpc } from "@/lib/trpc";
import type { DeviceListItem } from "@/lib/types";
import { cn, platformLabel } from "@/lib/utils";

export const Route = createFileRoute("/_app/devices/")({
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(context.trpc.device.list.queryOptions()),
  component: DevicesPage,
});

const PLATFORMS = ["all", "ios", "android"] as const;
type Platform = (typeof PLATFORMS)[number];

type DeviceStatus = DeviceListItem["status"];

/**
 * The status filter's options, in the order a person scanning for a phone
 * cares about them: free, taken, on its way back, then the ones that are no
 * use at all.
 */
const STATUSES: readonly DeviceStatus[] = [
  "ready",
  "busy",
  "preparing",
  "cleaning",
  "present",
  "unhealthy",
  "absent",
];

type View = "grid" | "list";

const VIEW_STORAGE_KEY = "yard.devices.view";

/**
 * Grid or list, remembered per browser like the device page's side panels.
 *
 * With nothing stored, a phone starts on the list: at 375px the grid is one
 * card per row, three or four devices to a screen, where list rows fit twice
 * that — and on a phone you are scanning for a free device, not browsing
 * them. A wider screen starts on the grid, which is what the width is for.
 */
function loadView(): View {
  try {
    const stored = window.localStorage.getItem(VIEW_STORAGE_KEY);
    if (stored === "grid" || stored === "list") return stored;
  } catch {
    // Private mode, or storage disabled. A default is a fine answer.
  }
  // `md`, the same line the shell draws between phone and desktop.
  return window.matchMedia("(min-width: 48rem)").matches ? "grid" : "list";
}

function saveView(view: View): void {
  try {
    window.localStorage.setItem(VIEW_STORAGE_KEY, view);
  } catch {
    /* not worth telling anyone about */
  }
}

// `min(100%, …)` so a column never asks for more than a 320px phone has.
// 340px is about Vercel's project card, which leaves the name room beside
// the logo, the state and the menu.
const GRID = "grid grid-cols-[repeat(auto-fill,minmax(min(100%,340px),1fr))] gap-6";

/** The list view's frame: one card, rows divided by hairlines. */
const LIST =
  "@container divide-y divide-gray-alpha-400 overflow-hidden rounded-lg bg-background-100 shadow-border";

function DevicesPage() {
  const { connected, pollInterval } = useDeviceStream();
  const { data: devices, isLoading } = useQuery({
    ...trpc.device.list.queryOptions(),
    refetchInterval: pollInterval,
  });
  const { data: me } = useQuery(trpc.user.me.queryOptions());
  const [q, setQ] = useState("");
  const [platform, setPlatform] = useState<Platform>("all");
  // What is filtered *out*, so a status no device has yet shown is still
  // let through by default.
  const [hidden, setHidden] = useState<ReadonlySet<DeviceStatus>>(() => new Set());
  const [view, setView] = useState<View>(loadView);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const matches = (devices ?? []).filter((d) => {
      if (platform !== "all" && d.platform !== platform) return false;
      if (hidden.has(d.status)) return false;
      if (!needle) return true;
      return [d.name, d.model, d.id, d.osVersion, d.manufacturer]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(needle));
    });
    // The devices you are holding sort to the front: they are the ones you came
    // back to the list for, and a farm is big enough that scanning for them is
    // the common case.
    return matches.sort(
      (a, b) => Number(b.reservation?.userId === me?.id) - Number(a.reservation?.userId === me?.id),
    );
  }, [devices, q, platform, hidden, me?.id]);

  const mineCount = useMemo(
    () => (devices ?? []).filter((d) => d.reservation?.userId === me?.id).length,
    [devices, me?.id],
  );

  const changeView = (next: View) => {
    setView(next);
    saveView(next);
  };

  const isAdmin = me?.isAdmin ?? false;

  return (
    <div className="flex flex-col">
      <PageHeader
        title="Devices"
        actions={
          <Button variant="secondary" asChild>
            <Link to="/my-phones">My phones</Link>
          </Button>
        }
        meta={
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1 leading-none">
            <span className="tabular-nums">
              {filtered.length} of {devices?.length ?? 0}
            </span>
            {mineCount > 0 && (
              <span className="flex items-center gap-1.5 font-medium text-mine tabular-nums">
                <StatusDot tone="mine" />
                {mineCount} yours
              </span>
            )}
            <LiveIndicator connected={connected} />
          </span>
        }
      />

      {/* Vercel's overview toolbar: search flexing, then the filters and the
          view toggle, all 40px. Where that row would squeeze the search — a
          phone, or a tablet beside the sidebar — it is two rows instead:
          search beside the view toggle, the two filters sharing the row below.
          The row wrappers turn into `contents` once the toolbar's own width
          (not the window's) has room for one row. */}
      <PageToolbar className="@container mb-4 block">
        <div className="flex flex-col gap-2 @2xl:flex-row @2xl:items-center">
          <div className="flex min-w-0 flex-1 gap-2 @2xl:contents">
            <Input
              aria-label="Filter devices"
              placeholder="Search name, model, udid…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              prefix={<Search className="size-4" />}
              prefixStyling={false}
              className="h-10 min-w-0 flex-1 *:data-[slot=input]:h-full"
            />
            <ViewToggle value={view} onChange={changeView} />
          </div>
          <div className="flex min-w-0 gap-2 @2xl:contents">
            <StatusFilter hidden={hidden} onChange={setHidden} />
            <PlatformFilter value={platform} onChange={setPlatform} />
          </div>
        </div>
      </PageToolbar>

      {isLoading ? (
        <LoadingState view={view} />
      ) : filtered.length === 0 ? (
        <NoDevices
          hasAny={(devices?.length ?? 0) > 0}
          isAdmin={isAdmin}
          onClear={() => {
            setQ("");
            setPlatform("all");
            setHidden(new Set());
          }}
        />
      ) : view === "grid" ? (
        <div className={GRID}>
          {filtered.map((d) => (
            <DeviceCard
              key={d.id}
              device={d}
              mine={Boolean(me) && d.reservation?.userId === me?.id}
              isAdmin={isAdmin}
            />
          ))}
        </div>
      ) : (
        // A container query rather than a breakpoint: whether the columns fit
        // depends on the sidebar as much as the window.
        <div className={LIST}>
          {filtered.map((d) => (
            <DeviceRow
              key={d.id}
              device={d}
              mine={Boolean(me) && d.reservation?.userId === me?.id}
              isAdmin={isAdmin}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Vercel's Status filter: every state's dot stacked on the trigger — hollow
 * once it is filtered out — and a count once anything is, over a menu of
 * checkboxes that stays open while you tick through it.
 */
function StatusFilter({
  hidden,
  onChange,
}: {
  hidden: ReadonlySet<DeviceStatus>;
  onChange: (hidden: ReadonlySet<DeviceStatus>) => void;
}) {
  const toggle = (status: DeviceStatus, shown: boolean) => {
    const next = new Set(hidden);
    if (shown) next.delete(status);
    else next.add(status);
    onChange(next);
  };
  const shownCount = STATUSES.length - hidden.size;

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="secondary"
          aria-label={
            hidden.size ? `Status: ${shownCount} of ${STATUSES.length} shown` : "Status: all shown"
          }
          className="h-10 min-w-0 flex-1 justify-between gap-2 px-3 font-normal @2xl:flex-none @2xl:gap-4"
        >
          <span className="flex min-w-0 items-center gap-2">
            <span aria-hidden="true" className="flex shrink-0 items-center pl-1">
              {STATUSES.map((s) => (
                <span
                  key={s}
                  className="-ml-1 flex rounded-full shadow-[0_0_0_1.5px_var(--ds-background-100)]"
                >
                  {hidden.has(s) ? (
                    <span className="size-2.5 rounded-full bg-background-100 shadow-[inset_0_0_0_1px_var(--ds-gray-alpha-500)]" />
                  ) : (
                    <StatusDot tone={DEVICE_STATUS[s].tone} />
                  )}
                </span>
              ))}
            </span>
            <span className="truncate">Status</span>
            {hidden.size > 0 && (
              <span className="flex h-5 shrink-0 items-center rounded-full bg-gray-alpha-300 px-1.5 text-label-13 tabular-nums">
                {shownCount}/{STATUSES.length}
              </span>
            )}
          </span>
          <ChevronDown className="size-4 text-gray-900" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={6} className="w-[200px]">
        {STATUSES.map((s) => (
          <DropdownMenuCheckboxItem
            key={s}
            checked={!hidden.has(s)}
            onCheckedChange={(checked) => toggle(s, checked === true)}
            // Ticking several states is the point; closing after each would
            // make that a reopen per state.
            onSelect={(e) => e.preventDefault()}
            className="h-8 gap-3 pointer-coarse:h-10"
          >
            <span className="flex items-center gap-2">
              <StatusDot tone={DEVICE_STATUS[s].tone} />
              {DEVICE_STATUS[s].label}
            </span>
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function PlatformFilter({
  value,
  onChange,
}: {
  value: Platform;
  onChange: (platform: Platform) => void;
}) {
  return (
    // Its natural width everywhere: on two rows the status filter takes what
    // is left of its row, and "Android" must never be squeezed for it.
    <SegmentedControl
      aria-label="Platform"
      value={value}
      onValueChange={(v) => onChange(v as Platform)}
      className="h-10 shrink-0"
    >
      {PLATFORMS.map((p) => (
        <SegmentedControlItem key={p} value={p} className="px-2.5 @2xl:px-3">
          {p === "all" ? "All" : platformLabel(p)}
        </SegmentedControlItem>
      ))}
    </SegmentedControl>
  );
}

function ViewToggle({ value, onChange }: { value: View; onChange: (view: View) => void }) {
  return (
    <SegmentedControl
      aria-label="View"
      value={value}
      onValueChange={(v) => onChange(v as View)}
      className="h-10 shrink-0 @2xl:order-last"
    >
      <SegmentedControlItem value="grid" aria-label="Grid view" className="w-10 px-0">
        <LayoutGrid />
      </SegmentedControlItem>
      <SegmentedControlItem value="list" aria-label="List view" className="w-10 px-0">
        <List />
      </SegmentedControlItem>
    </SegmentedControl>
  );
}

/**
 * Vercel's loading state: the real toolbar over shells of the cards and rows
 * to come, their text drawn as rounded grey bars on the same 20px lines, so
 * nothing moves when the data lands.
 */
function LoadingState({ view }: { view: View }) {
  if (view === "grid") {
    return (
      <div className={GRID}>
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="flex flex-col rounded-lg bg-background-100 p-4 shadow-border">
            <div className="flex items-center gap-4">
              <Skeleton shape="circle" className="size-8" />
              <div className="flex flex-1 flex-col gap-0.5">
                <SkeletonLine className="w-28" />
                <SkeletonLine className="w-20" />
              </div>
            </div>
            <Skeleton shape="pill" className="mt-3 h-[22px] w-28" />
            <div className="mt-2.5 flex flex-col gap-0.5">
              <SkeletonLine className="w-24" />
              <SkeletonLine className="w-52" />
            </div>
          </div>
        ))}
      </div>
    );
  }
  // The row's columns at the row's proportions, so the bars sit where the
  // name, state, platform, provider and holder will.
  return (
    <div className={LIST}>
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="flex items-start gap-4 py-3 pr-3 pl-4 @4xl:items-center @4xl:py-4">
          <div className="flex min-w-0 flex-[1.4] flex-col">
            <SkeletonLine className="w-28" />
            <SkeletonLine className="w-20" />
            <SkeletonLine className="w-36 @4xl:hidden" />
          </div>
          <div className="flex min-w-0 flex-1 flex-col items-end @4xl:items-start">
            <SkeletonLine className="w-14" />
            <SkeletonLine className="hidden w-10 @4xl:flex" />
          </div>
          <div className="hidden min-w-0 flex-[1.4] items-center gap-3 @4xl:flex">
            <Skeleton shape="circle" className="size-6" />
            <SkeletonLine className="w-20" />
          </div>
          <div className="hidden min-w-0 flex-[2] flex-col @4xl:flex">
            <SkeletonLine className="w-28" />
            <SkeletonLine className="w-44" />
          </div>
          <div className="hidden min-w-0 flex-[1.6] items-center justify-end gap-3 @4xl:flex">
            <SkeletonLine className="w-24" />
            <Skeleton shape="circle" className="size-6" />
          </div>
          <span className="size-8 shrink-0" />
        </div>
      ))}
    </div>
  );
}

/** A bar of text-to-come, on a line as tall as the text's. */
function SkeletonLine({ className }: { className?: string }) {
  return (
    <span className={cn("flex h-5 max-w-full items-center", className)}>
      <Skeleton className="h-3.5 w-full" />
    </span>
  );
}

function NoDevices({
  hasAny,
  isAdmin,
  onClear,
}: {
  hasAny: boolean;
  isAdmin: boolean;
  onClear: () => void;
}) {
  return (
    <EmptyState
      className="rounded-lg border-0 shadow-border"
      icon={hasAny ? <SearchX /> : <Smartphone />}
      title={hasAny ? "No devices match your filters" : "No devices yet"}
      description={
        hasAny
          ? "Try a different search, or clear the status and platform filters."
          : "Devices appear here once a provider connects and reports its inventory. Create a provider token under Providers to get started."
      }
    >
      {hasAny ? (
        <Button variant="secondary" className="h-10" onClick={onClear}>
          Clear Filters
        </Button>
      ) : (
        // Only an admin can follow that advice; anyone else would bounce off
        // the admin guard back here.
        isAdmin && (
          <Button asChild variant="secondary" className="h-10">
            <Link to="/admin/providers">Go to Providers</Link>
          </Button>
        )
      )}
    </EmptyState>
  );
}

/** Distinguishes "nothing is changing" from "we stopped hearing about changes". */
function LiveIndicator({ connected }: { connected: boolean }) {
  return (
    <span
      className="flex items-center gap-1.5 leading-none"
      title={connected ? "Receiving live updates" : "Live stream unavailable — polling every 5s"}
    >
      <StatusDot tone={connected ? "success" : "muted"} />
      {connected ? "Live" : "Polling"}
    </span>
  );
}
